import * as React from 'react';
import { useSelector } from 'react-redux';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import useDebouncedState from 'hooks/useDebouncedState';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { PrimaryButton } from '@fluentui/react';
import { SelectOption } from 'formComponents/Select/types';
import { ValueType } from 'react-select';
import { Select } from 'formComponents/Select/Select';
import { authSelector } from 'store/auth/selector';
import { Label } from '@fluentui/react/lib/Label';
import { generateReportPrintUrl } from '../utils';
import {
    FilterValues,
    HochrechnungPeriod,
    HochrechnungSourceEntity,
    periodOptions,
    sourceEntityOptions,
} from './types';
import { Hochrechnung } from './Hochrechnung';

const styles = mergeStyleSets({
    container: {
        margin: 0,
        marginTop: 5,
        padding: 0,
        display: 'grid',
        gridTemplateRows: 'max-content max-content 1fr',
        gridRowGap: 5,
        height: 'calc(100vh - 75px)',
    },
    searchLine1: {
        display: 'grid',
        gridTemplateColumns: '130px 160px 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    actionButtons: {
        marginTop: 29,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, max-content)',
        gridColumnGap: 10,
    },
});

const defaultFilterValues: FilterValues = {
    sourceEntity: 'auftrag',
    period: 'calendaryear',
};

export const HochrechnungView: React.FC = () => {
    const auth = useSelector(authSelector);
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(
        {
            ...defaultFilterValues,
        },
        2000,
    );

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.searchLine1}>
                <div>
                    <Label>Auftrag/Rechnung</Label>
                    <Select
                        name="sourceEntity"
                        value={sourceEntityOptions.find((o) => o.value === filterValues.sourceEntity)}
                        options={sourceEntityOptions}
                        onChange={(option: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                sourceEntity: (option?.value || 'auftrag') as HochrechnungSourceEntity,
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Zeitraum</Label>
                    <Select
                        name="period"
                        value={periodOptions.find((o) => o.value === filterValues.period)}
                        options={periodOptions}
                        onChange={(option: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                period: (option?.value || 'calendaryear') as HochrechnungPeriod,
                            });
                        }}
                    />
                </div>

                <div />

                <div className={styles.actionButtons}>
                    {auth.rolle !== 'AD' && (
                        <PrimaryButton
                            onClick={() => {
                                window.open(
                                    generateReportPrintUrl('hochrechnung', {
                                        sourceEntity: filterValues.sourceEntity,
                                        period: filterValues.period,
                                    }),
                                );
                            }}
                            text="Drucken"
                        />
                    )}
                </div>
            </div>

            <Hochrechnung sourceEntity={debouncedFilterValues.sourceEntity} period={debouncedFilterValues.period} />
        </div>
    );
};
